import { useMemo } from 'react'
import type { Task, Section } from '@/src/renderer/lib/db/models'
import type { RenderSegment } from '../../ProjectPage/components/ProjectSegment/ProjectSegment.types'
import { useMidnightRefresh } from '../../../hooks/useMidnightRefresh'

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000

/** Stable synthetic Section IDs used to render virtual Quick Notes buckets. */
export const QUICK_NOTES_SECTION_IDS = {
  recent: '__quick_notes_recent__',
  older: '__quick_notes_older__',
} as const

/**
 * Compute virtual segments for Quick Notes: two date-based buckets.
 *
 * Tasks created within the last 7 days go into `"Letzte 7 Tage"`, everything
 * older goes into `"Älter"`. The Section objects are synthetic — they don't
 * exist in the DB. SectionHeader renders their labels via i18n keyed on
 * the stable IDs above.
 *
 * Empty buckets are omitted — a header only appears when its bucket contains
 * at least one task. Collapsed state is read via `isCollapsed`.
 *
 * Rebuckets automatically at midnight via `useMidnightRefresh`.
 */
export function useQuickNotesSegments(
  tasks: Task[] | undefined,
  recentTitle: string,
  olderTitle: string,
  isCollapsed: (sectionId: string) => boolean,
): RenderSegment[] {
  const dateKey = useMidnightRefresh()

  return useMemo(() => {
    void dateKey // triggers recompute at midnight
    if (!tasks) return []

    const now = Date.now()
    const recent: Task[] = []
    const older: Task[] = []

    for (const task of tasks) {
      const createdAtMs = task.createdAt instanceof Date
        ? task.createdAt.getTime()
        : new Date(task.createdAt).getTime()
      if (now - createdAtMs <= SEVEN_DAYS_MS) {
        recent.push(task)
      } else {
        older.push(task)
      }
    }

    recent.sort((a, b) => a.order - b.order)
    older.sort((a, b) => a.order - b.order)

    const makeVirtualSection = (id: string, title: string): Section => ({
      id,
      title,
      projectId: '__virtual__',
      order: 0,
      collapsed: isCollapsed(id),
      createdAt: new Date(0),
      updatedAt: new Date(0),
      lastModified: new Date(0),
      synced: true,
      syncedAt: null,
      userId: null,
      cloudId: null,
      deletedAt: null,
    })

    const result: RenderSegment[] = []
    if (recent.length > 0) {
      // Collapsed segment: `tasks` is empty (external consumers see no
      // tasks), but `allTasks` keeps the full list so the TaskList stays
      // mounted across collapse/expand — avoids ~150ms remount jank.
      const recentCollapsed = isCollapsed(QUICK_NOTES_SECTION_IDS.recent)
      result.push({
        section: makeVirtualSection(QUICK_NOTES_SECTION_IDS.recent, recentTitle),
        tasks: recentCollapsed ? [] : recent,
        allTasks: recent,
        taskCount: recent.length,
        collapsed: recentCollapsed,
      })
    }
    if (older.length > 0) {
      const olderCollapsed = isCollapsed(QUICK_NOTES_SECTION_IDS.older)
      result.push({
        section: makeVirtualSection(QUICK_NOTES_SECTION_IDS.older, olderTitle),
        tasks: olderCollapsed ? [] : older,
        allTasks: older,
        taskCount: older.length,
        collapsed: olderCollapsed,
      })
    }
    // Empty page: return a headerless anchor segment so AddTask and the
    // create-shortcuts (A, B, Cmd+N) have a segment to render into.
    //
    // Uses the same section identity as the "recent" bucket (`hideHeader: true`
    // suppresses the visible header + skips nav). Keeping the section.id stable
    // across the 0→1 task transition prevents ProjectSegment from remounting
    // (which would discard focus/selection of the newly created task).
    if (result.length === 0) {
      return [{
        section: makeVirtualSection(QUICK_NOTES_SECTION_IDS.recent, recentTitle),
        tasks: [],
        taskCount: 0,
        hideHeader: true,
      }]
    }
    return result
  }, [tasks, recentTitle, olderTitle, dateKey, isCollapsed])
}
